import { useEffect, useState } from "react";
import { api, apiError } from "../../lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";
import { Button } from "../ui/button";
import { Textarea } from "../ui/textarea";
import { StatusBadge, PriorityBadge, LabelBadge, Avatar } from "../badges";
import AttachmentsPanel from "../AttachmentsPanel";
import { toast } from "sonner";

export default function IssueDetail({ issue, users, onClose, onChanged }) {
  const [comments, setComments] = useState([]);
  const [body, setBody] = useState("");
  const userById = (id) => users.find((u) => u.id === id);

  const loadComments = () =>
    api.get("/comments", { params: { entity_type: "issue", entity_id: issue.id } }).then((r) => setComments(r.data));

  useEffect(() => { loadComments(); }, [issue.id]); // eslint-disable-line

  const comment = async () => {
    if (!body.trim()) return;
    try {
      await api.post("/comments", { entity_type: "issue", entity_id: issue.id, body });
      setBody(""); loadComments(); onChanged();
    } catch (err) { toast.error(apiError(err)); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-xl max-h-[85vh] overflow-y-auto" data-testid="issue-detail-modal">
        <DialogHeader>
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-mono text-xs text-primary">#{issue.number}</span>
            <StatusBadge status={issue.status} />
            <PriorityBadge priority={issue.priority} />
            {(issue.labels || []).map((l) => <LabelBadge key={l} label={l} />)}
          </div>
          <DialogTitle className="font-heading text-left">{issue.title}</DialogTitle>
        </DialogHeader>
        <p className="text-sm text-muted-foreground">{issue.description || "No description."}</p>
        <p className="text-xs font-mono text-muted-foreground">
          Opened by {issue.created_by_name} · {new Date(issue.created_at).toLocaleString()}
          {issue.assignee_id && <> · assigned to {userById(issue.assignee_id)?.name}</>}
        </p>

        <div className="border-t border-border pt-4">
          <AttachmentsPanel entityType="issue" entityId={issue.id} compact />
        </div>

        <div className="border-t border-border pt-4 space-y-3">
          <p className="text-xs font-mono uppercase tracking-wider text-muted-foreground">Discussion ({comments.length})</p>
          {comments.map((c) => (
            <div key={c.id} className="flex gap-2.5" data-testid="issue-comment-item">
              <Avatar name={c.author_name} size="sm" />
              <div className="bg-accent/60 rounded-md px-3 py-2 flex-1 min-w-0">
                <p className="text-xs">
                  <span className="font-medium">{c.author_name}</span>{" "}
                  <span className="text-muted-foreground font-mono text-[10px]">{new Date(c.created_at).toLocaleString()}</span>
                </p>
                <p className="text-sm mt-0.5">{c.body}</p>
              </div>
            </div>
          ))}
          <div className="flex gap-2">
            <Textarea data-testid="issue-comment-input" placeholder="Leave a comment... use @name to mention someone" value={body}
              onChange={(e) => setBody(e.target.value)} className="min-h-[60px]" />
          </div>
          <Button size="sm" data-testid="issue-comment-submit-btn" onClick={comment} disabled={!body.trim()}>Comment</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
